import React, { useEffect, useRef, useState } from 'react';

import { CloseIcon } from '../../components/icons/Close';
import { MinusIcon } from '../../components/icons/Minus';
import { PlusIcon } from '../../components/icons/Plus';
import { SearchIcon } from '../../components/icons/Search';
import { SportIcon, sportIconKey } from '../../components/SportIcon/SportIcon';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import './RosterFilterBar.css';

export type SportOption = {
    name: string;
    icon: string | null;
};

type RosterFilterBarProps = {
    sports: SportOption[];
    query: string;
    selectedSports: string[];
    onQueryChange: (query: string) => void;
    onToggleSport: (name: string) => void;
    onClearSports: () => void;
};

type Mode = 'idle' | 'search' | 'sport';

const BASE_BOTTOM = 24;
const GAP_ABOVE_FOOTER = 8;
const SEARCH_ANIM_MS = 280;
const PANEL_ANIM_MS = 220;

// Filter-by-sport is temporarily disabled (search-only). Flip to true to restore
// the "filter" pill + sport panel; all the filter/sport code below is kept intact.
const FILTER_ENABLED = false;

// Mount/enter/exit transition, mirroring the cart drawer: keep the node mounted
// for the exit animation, flip the `visible` flag a frame after mount so the
// enter transition runs from its start state.
function useMountTransition(open: boolean, durationMs: number) {
    const [render, setRender] = useState(open);
    const [visible, setVisible] = useState(open);
    const timer = useRef<number | null>(null);
    const raf = useRef<number | null>(null);

    useEffect(() => {
        if (open) {
            if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; }
            setRender(true);
            raf.current = requestAnimationFrame(() => {
                raf.current = requestAnimationFrame(() => setVisible(true));
            });
            return () => {
                if (raf.current !== null) { cancelAnimationFrame(raf.current); raf.current = null; }
            };
        }
        setVisible(false);
        timer.current = window.setTimeout(() => setRender(false), durationMs);
        return undefined;
    }, [open, durationMs]);

    useEffect(() => () => {
        if (timer.current !== null) { window.clearTimeout(timer.current); }
        if (raf.current !== null) { cancelAnimationFrame(raf.current); }
    }, []);

    return { render, visible };
}

function SportThumb({ option }: { option: SportOption }) {
    if (option.icon) {
        return <img className="roster-filter__sport-icon" src={option.icon} alt="" aria-hidden="true" />;
    }
    return (
        <span className="roster-filter__sport-icon" aria-hidden="true">
            <SportIcon iconKey={sportIconKey(option.name)} />
        </span>
    );
}

function CheckMark() {
    return (
        <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2.5 6.4L4.8 8.7L9.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// Floating, centered filter bar pinned above the page footer.
// idle:   [ filter ] [ search ]
// search: [ search input ] [ x ]      -> filter athletes by name
// sport:  [ sport (n) - ] [ x ]       -> multi-select sport checklist opens above
export function RosterFilterBar({
    sports, query, selectedSports, onQueryChange, onToggleSport, onClearSports,
}: RosterFilterBarProps) {
    const [mode, setMode] = useState<Mode>('idle');
    const [listOpen, setListOpen] = useState(false);
    const [bottom, setBottom] = useState(BASE_BOTTOM);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const search = useMountTransition(mode === 'search', SEARCH_ANIM_MS);
    const panel = useMountTransition(mode === 'sport' && listOpen && sports.length > 0, PANEL_ANIM_MS);

    const isTouch = useMediaQuery('(pointer: coarse)');

    useEffect(() => {
        if (search.visible) { inputRef.current?.focus(); }
    }, [search.visible]);

    // Keep the bar resting just above the footer instead of overlapping it.
    // A ResizeObserver on the body catches layout changes that fire no scroll/resize
    // event - e.g. filtering down to one result, which shrinks the page.
    useEffect(() => {
        if (isTouch) { return; }
        const footer = document.querySelector('.site-footer');
        if (!footer) { return; }
        let raf = 0;
        const update = () => {
            raf = 0;
            const overlap = window.innerHeight - footer.getBoundingClientRect().top;
            setBottom(Math.max(BASE_BOTTOM, overlap + GAP_ABOVE_FOOTER));
        };
        const schedule = () => { if (!raf) { raf = requestAnimationFrame(update); } };
        schedule();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        const ro = new ResizeObserver(schedule);
        ro.observe(document.body);
        return () => {
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            ro.disconnect();
            if (raf) { cancelAnimationFrame(raf); }
        };
    }, [isTouch]);

    function openSearch() {
        onClearSports();
        setListOpen(false);
        setMode('search');
    }

    function openSport() {
        onQueryChange('');
        setMode('sport');
        setListOpen(false);
    }

    function close() {
        if (mode === 'search') { onQueryChange(''); } else { onClearSports(); }
        setListOpen(false);
        setMode('idle');
    }

    const squareActive = mode !== 'idle' || search.render;

    return (
        <div className="roster-filter" style={isTouch ? undefined : { bottom: `${bottom}px` }}>
            {panel.render && (
                <div
                    className={`roster-filter__panel${panel.visible ? ' roster-filter__panel--open' : ''}`}
                    role="group"
                    aria-label="Filter by sport"
                >
                    {sports.map((option) => {
                        const active = selectedSports.includes(option.name);
                        return (
                            <button
                                key={option.name}
                                type="button"
                                role="checkbox"
                                aria-checked={active}
                                className={`roster-filter__option${active ? ' roster-filter__option--active' : ''}`}
                                onClick={() => onToggleSport(option.name)}
                            >
                                <span className={`roster-filter__check${active ? ' roster-filter__check--on' : ''}`}>
                                    {active && <CheckMark />}
                                </span>
                                <span className="roster-filter__option-body">
                                    <SportThumb option={option} />
                                    <span className="roster-filter__option-label">{option.name}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="roster-filter__track">
                {search.render ? (
                    <label className={`roster-filter__field${search.visible ? ' roster-filter__field--open' : ''}`}>
                        <SearchIcon className="roster-filter__field-icon" />
                        <input
                            ref={inputRef}
                            type="text"
                            className="roster-filter__input"
                            placeholder="search"
                            aria-label="Search athletes by name"
                            value={query}
                            onChange={(e) => onQueryChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.currentTarget.blur();
                                }
                            }}
                        />
                    </label>
                ) : mode === 'sport' ? (
                    <button
                        type="button"
                        className="roster-filter__btn roster-filter__btn--wide roster-filter__btn--enter"
                        onClick={() => setListOpen((v) => !v)}
                        aria-expanded={listOpen}
                    >
                        <span className="roster-filter__btn-label">sport</span>
                        {selectedSports.length > 0 && (
                            <span className="roster-filter__count">{selectedSports.length}</span>
                        )}
                        {listOpen
                            ? <MinusIcon className="roster-filter__btn-affix" />
                            : <PlusIcon className="roster-filter__btn-affix" />}
                    </button>
                ) : FILTER_ENABLED ? (
                    <button type="button" className="roster-filter__btn roster-filter__btn--enter" onClick={openSport}>
                        <span className="roster-filter__btn-label">filter</span>
                    </button>
                ) : (
                    <button type="button" className="roster-filter__btn roster-filter__btn--enter" onClick={openSearch}>
                        <span className="roster-filter__btn-label">search</span>
                    </button>
                )}

                <button
                    type="button"
                    className="roster-filter__square"
                    onClick={mode === 'idle' ? openSearch : close}
                    aria-label={squareActive ? 'Close' : 'Search athletes'}
                >
                    <span className={`roster-filter__square-icons${squareActive ? ' is-close' : ''}`}>
                        <SearchIcon className="roster-filter__square-icon roster-filter__icon--search" />
                        <CloseIcon className="roster-filter__square-icon roster-filter__icon--close" />
                    </span>
                </button>
            </div>
        </div>
    );
}
